import { useScope } from "@state/scopeContext.lib";
import { useActiveWorkspace } from "./useActiveWorkspace";

interface UseIsGlFirstResult {
    /**
     * Whether the active workspace is GL-first.
     *
     * This gates category-related UI: GL-first workspaces hide it, non-GL-first
     * workspaces show it. The value drives that gating directly (e.g.
     * `!enabled && <CategoryUI />`).
     *
     * While the workspace is still loading (`initializing` is `true`) this is
     * `true` — i.e. we optimistically assume GL-first — rather than `false`.
     * This is the better default because of how the two cases look to the user:
     *
     * - GL-first workspaces: category UI stays hidden the whole time, so there
     *   is no flash of category UI that then disappears once the workspace
     *   loads.
     * - Non-GL-first workspaces: category UI simply appears with a brief delay
     *   once the workspace is known.
     *
     * A short delay before showing UI is far less jarring than showing UI and
     * then yanking it away, so we bias toward hiding (treat-as-GL-first) until
     * we know for certain.
     */
    enabled: boolean;
    /**
     * Whether the GL-first state is still being determined because the active
     * workspace has not loaded yet.
     *
     * Simple cases that only hide category-related UI can ignore this and rely
     * on `enabled` (which defaults to `true` while loading). More complex cases
     * that act imperatively on the GL-first state (e.g. an effect that switches
     * tabs) should wait until `initializing` is `false` so they don't act on
     * the optimistic default.
     */
    initializing: boolean;
}

/**
 * Returns the GL-first state of the active workspace.
 *
 * See {@link UseIsGlFirstResult} for the meaning of `enabled` and
 * `initializing`.
 */
export function useIsGlFirst(): UseIsGlFirstResult {
    const { workspaceId } = useScope();
    const workspace = useActiveWorkspace();

    if (!workspaceId) {
        return { enabled: false, initializing: false };
    }

    if (workspace == null) {
        return { enabled: true, initializing: true };
    }

    return {
        enabled: workspace.glFirstEnabledAt != null,
        initializing: false,
    };
}
